import { useState, useEffect } from 'react';
import { useAppSelector } from '../../store/hooks';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  LocationOn as LocationOnIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { sessionApi, Session } from '../../api/session.api';
import { format, isPast, isToday, isTomorrow } from 'date-fns';
import { SessionFormDialog } from '../sessions/SessionFormDialog';

interface SquadSessionsProps {
  squadId: string;
}

export const SquadSessions = ({ squadId }: SquadSessionsProps) => {
  const { user } = useAppSelector((state) => state.auth);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSession, setEditingSession] = useState<Session | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const canManageSessions = user?.role === 'ADMIN' || user?.role === 'HEAD_COACH' || user?.role === 'COACH';

  useEffect(() => {
    fetchSessions();
  }, [squadId]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await sessionApi.getSessionsBySquad(squadId, {
        status: undefined,
      });
      setSessions(response.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = () => {
    setEditingSession(null);
    setDialogOpen(true);
  };

  const handleEditSession = (session: Session) => {
    setEditingSession(session);
    setDialogOpen(true);
  };

  const handleOpenCancelDialog = (session: Session) => {
    setSelectedSession(session);
    setCancelDialogOpen(true);
    setCancelReason('');
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setSelectedSession(null);
    setCancelReason('');
  };

  const handleCancelSession = async () => {
    if (!selectedSession) return;

    setError('');
    setSuccess('');

    try {
      await sessionApi.cancelSession(selectedSession.id, cancelReason);
      setSuccess('Session cancelled successfully');
      handleCloseCancelDialog();
      fetchSessions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to cancel session');
    }
  };

  const handleCompleteSession = async (sessionId: string) => {
    setError('');
    setSuccess('');

    try {
      await sessionApi.completeSession(sessionId);
      setSuccess('Session marked as completed');
      fetchSessions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to complete session');
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (!window.confirm('Are you sure you want to delete this session?')) return;

    setError('');
    setSuccess('');

    try {
      await sessionApi.deleteSession(sessionId);
      setSuccess('Session deleted successfully');
      fetchSessions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete session');
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <Chip label="Scheduled" color="info" size="small" />;
      case 'in_progress':
        return <Chip label="In Progress" color="warning" size="small" />;
      case 'completed':
        return <Chip label="Completed" color="success" size="small" icon={<CheckCircleIcon />} />;
      case 'cancelled':
        return <Chip label="Cancelled" color="error" size="small" icon={<CancelIcon />} />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const formatSessionDate = (dateString: string) => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return `Today, ${format(date, 'h:mm a')}`;
    } else if (isTomorrow(date)) {
      return `Tomorrow, ${format(date, 'h:mm a')}`;
    } else {
      return format(date, 'EEE, MMM d, h:mm a');
    }
  };

  const upcomingSessions = sessions.filter(
    (s) => s.status === 'scheduled' && !isPast(new Date(s.startTime))
  );
  const pastSessions = sessions.filter(
    (s) => s.status === 'completed' || (s.status === 'scheduled' && isPast(new Date(s.startTime)))
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Training Sessions
          </Typography>
          {canManageSessions && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleCreateSession}
            >
              Create Session
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {sessions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No sessions scheduled yet
          </Typography>
        ) : (
          <>
            {upcomingSessions.length > 0 && (
              <>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Upcoming ({upcomingSessions.length})
                </Typography>
                <List>
                  {upcomingSessions.map((session) => (
                    <ListItem key={session.id} divider>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1">{session.title}</Typography>
                            <Chip label={session.sessionType} size="small" />
                            {getStatusChip(session.status)}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography component="span" variant="body2">
                              <AccessTimeIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                              {formatSessionDate(session.startTime)} - {format(new Date(session.endTime), 'h:mm a')}
                            </Typography>
                            {session.location && (
                              <>
                                {' • '}
                                <Typography component="span" variant="body2">
                                  <LocationOnIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                  {session.location}
                                </Typography>
                              </>
                            )}
                            {session.description && (
                              <>
                                <br />
                                <Typography component="span" variant="caption" color="text.secondary">
                                  {session.description}
                                </Typography>
                              </>
                            )}
                          </>
                        }
                      />
                      {canManageSessions && (
                        <ListItemSecondaryAction>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleEditSession(session)}
                              title="Edit"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              edge="end"
                              size="small"
                              color="success"
                              onClick={() => handleCompleteSession(session.id)}
                              title="Mark as Complete"
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              edge="end"
                              size="small"
                              color="error"
                              onClick={() => handleOpenCancelDialog(session)}
                              title="Cancel Session"
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              edge="end"
                              size="small"
                              color="warning"
                              onClick={() => handleDeleteSession(session.id)}
                              title="Delete"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {pastSessions.length > 0 && (
              <>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
                  Past Sessions ({pastSessions.length})
                </Typography>
                <List>
                  {pastSessions.slice(0, 5).map((session) => (
                    <ListItem key={session.id} divider sx={{ opacity: 0.7 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1">{session.title}</Typography>
                            {getStatusChip(session.status)}
                          </Box>
                        }
                        secondary={format(new Date(session.startTime), 'EEE, MMM d, yyyy • h:mm a')}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </>
        )}
      </CardContent>

      {/* Session Form Dialog */}
      {dialogOpen && (
        <SessionFormDialog
          open={dialogOpen}
          onClose={(refreshNeeded) => {
            setDialogOpen(false);
            setEditingSession(null);
            if (refreshNeeded) fetchSessions();
          }}
          session={editingSession}
          defaultSquadId={squadId}
        />
      )}

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onClose={handleCloseCancelDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Session</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to cancel "{selectedSession?.title}"?
          </Typography>
          <TextField
            fullWidth
            required
            label="Cancellation Reason"
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Provide a reason for cancellation..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog}>Cancel</Button>
          <Button
            onClick={handleCancelSession}
            color="error"
            variant="contained"
            disabled={!cancelReason.trim()}
          >
            Cancel Session
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
